import React, { useEffect, useState } from "react";
import AdminLayout from "../components/AdminLayout";
import { api, API } from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

const VERTICALS = ["law_enforcement", "fire_service", "ems", "dispatch_psap", "corrections", "school_safety", "military", "emergency_medicine"];
const BEATS = ["acute_crisis", "moral_injury", "survivor_guilt", "secondary_trauma", "relationship_rupture", "identity_erosion", "grief_processing", "boundary_violation", "anger_dysregulation", "help_seeking_resistance", "reintegration", "purpose_and_meaning"];
const MODALITIES = ["narrative", "reflective", "supportive", "directive", "action_oriented"];

export default function AgencyProductionQueue() {
  const [items, setItems] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [form, setForm] = useState({
    vertical: "law_enforcement",
    incident_category: "",
    emotional_beat: "secondary_trauma",
    modality_primary: "reflective",
    avatar_pairing: "partner_officer",
    setting: "patrol_vehicle_post_call",
    generate_image: false,
  });

  const load = () => api.get("/agency/production-queue").then(r => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const generate = async () => {
    if (!form.incident_category) { toast.error("Incident category required."); return; }
    setGenerating(true);
    try {
      await api.post("/agency/production-queue/generate", form);
      toast.success("Scenario generated and queued for clinical review.");
      load();
    } catch (e) {
      toast.error("Generation failed.");
    } finally { setGenerating(false); }
  };

  return (
    <AdminLayout>
      <div className="haruki-fade-in">
        <h1 className="font-heading text-3xl font-bold">Production Queue</h1>
        <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
          Generate new scenarios. Pipeline: Claude script → HMDE Call 1 → Gemini Nano Banana image → Clinical review.
        </p>

        <Card className="mt-8">
          <CardHeader><CardTitle className="font-heading text-lg">Generate new scenario</CardTitle></CardHeader>
          <CardContent className="grid gap-4 sm:grid-cols-2">
            <Field label="Vertical">
              <Select value={form.vertical} onValueChange={(v) => setForm({ ...form, vertical: v })}>
                <SelectTrigger data-testid="prod-vertical-select"><SelectValue /></SelectTrigger>
                <SelectContent>{VERTICALS.map(v => <SelectItem key={v} value={v}>{v.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Incident category">
              <Input data-testid="prod-category-input" value={form.incident_category} onChange={(e) => setForm({ ...form, incident_category: e.target.value })} placeholder="e.g. pediatric_resuscitation" />
            </Field>
            <Field label="Emotional beat">
              <Select value={form.emotional_beat} onValueChange={(v) => setForm({ ...form, emotional_beat: v })}>
                <SelectTrigger data-testid="prod-beat-select"><SelectValue /></SelectTrigger>
                <SelectContent>{BEATS.map(v => <SelectItem key={v} value={v}>{v.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Modality">
              <Select value={form.modality_primary} onValueChange={(v) => setForm({ ...form, modality_primary: v })}>
                <SelectTrigger data-testid="prod-modality-select"><SelectValue /></SelectTrigger>
                <SelectContent>{MODALITIES.map(v => <SelectItem key={v} value={v}>{v.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Avatar pairing">
              <Input data-testid="prod-avatar-input" value={form.avatar_pairing} onChange={(e) => setForm({ ...form, avatar_pairing: e.target.value })} />
            </Field>
            <Field label="Setting">
              <Input data-testid="prod-setting-input" value={form.setting} onChange={(e) => setForm({ ...form, setting: e.target.value })} />
            </Field>
            <div className="flex items-center gap-2 sm:col-span-2">
              <Checkbox id="img" data-testid="prod-image-checkbox" checked={form.generate_image} onCheckedChange={(v) => setForm({ ...form, generate_image: !!v })} />
              <Label htmlFor="img" className="cursor-pointer">Generate scenario image (Nano Banana)</Label>
            </div>
            <Button
              data-testid="prod-generate-button"
              onClick={generate}
              disabled={generating}
              className="sm:col-span-2"
              style={{ background: "hsl(var(--haruki-leaf))", color: "white" }}
            >
              {generating ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Generating…</> : "Generate scenario"}
            </Button>
          </CardContent>
        </Card>

        <h2 className="mt-10 font-heading text-xl font-semibold">Queue ({items.length})</h2>
        <div className="mt-4 space-y-3">
          {items.map(i => (
            <Card key={i.queue_id} data-testid={`queue-row-${i.queue_id}`}>
              <CardContent className="p-5">
                <div className="flex flex-wrap items-center gap-3 text-xs">
                  <span className="rounded-full px-2 py-1 capitalize" style={{ background: "hsl(var(--secondary))" }}>{i.status.replace(/_/g, " ")}</span>
                  <span className="text-muted-foreground">{i.vertical.replace(/_/g, " ")}</span>
                  <span className="text-muted-foreground">·</span>
                  <span className="text-muted-foreground">{i.incident_category.replace(/_/g, " ")}</span>
                  <span className="text-muted-foreground">·</span>
                  <span className="text-muted-foreground">HMDE content: {Math.round(i.hmde_content_score || 0)}</span>
                </div>
                <p className="mt-3 text-sm">{i.script}</p>
                {i.image_url && (
                  <img
                    src={i.image_url.startsWith("http") ? i.image_url : `${process.env.REACT_APP_BACKEND_URL}${i.image_url}`}
                    alt="" className="mt-3 aspect-video w-full max-w-md rounded-md border border-border object-cover"
                  />
                )}
              </CardContent>
            </Card>
          ))}
          {items.length === 0 && <Card><CardContent className="p-6 text-sm text-muted-foreground">Queue is empty.</CardContent></Card>}
        </div>
      </div>
    </AdminLayout>
  );
}

const Field = ({ label, children }) => (
  <div className="space-y-2">
    <Label>{label}</Label>
    {children}
  </div>
);
